"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  DollarSign,
  Pencil,
  Globe,
  Copy,
  Check,
  ImagePlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import EditBusinessModal from "./EditBusinessModal";
import BusinessPhotosModal from "./BusinessPhotosModal";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";
import { getAddressFromUrl } from "../../utils/location/getAddressFromUrl";

const API_URL = REST_API_URL;

export default function YourBusinessMyBusiness() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addressText, setAddressText] = useState("");
  const [loadingAddress, setLoadingAddress] = useState(false);
  const fileInputRef = useRef(null);

  const businessLink = business?.bookly_link;

  useEffect(() => {
    let isCancelled = false;

    if (!business?.address_url) {
      setAddressText("");
      return;
    }

    setLoadingAddress(true);
    getAddressFromUrl(business.address_url)
      .then((addr) => {
        if (!isCancelled) {
          setAddressText(addr);
        }
      })
      .catch((err) => {
        console.error("Failed to get address:", err);
        if (!isCancelled) {
          setAddressText("");
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setLoadingAddress(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [business?.address_url]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(businessLink);
    setCopied(true);
    toast.success(t("home.linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  const fetchUserInfo = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_URL}/auth/userinfo/`, {
        headers: buildHeaders(),
      });

      if (res.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBusiness(data);
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(t("home.failedLoadUserInfo"));
    } finally {
      setLoading(false);
    }
  }, [token, navigate, t]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await fetch(`${API_URL}/service/currencies/`, {
          headers: buildHeaders(),
        });
        const data = await res.json();
        if (Array.isArray(data)) setCurrencies(data);
      } catch (err) {
        console.warn("⚠️ Currency fetch failed:", err);
      }
    };
    fetchCurrencies();
  }, [token]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchUserInfo();
    };
    window.addEventListener("userUpdated", handleUpdate);
    return () => window.removeEventListener("userUpdated", handleUpdate);
  }, [fetchUserInfo]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-[320px] bg-white rounded-2xl shadow text-gray-400">
        {t("home.loadingUserInfo")}
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-[320px] bg-white rounded-2xl shadow text-red-500">
        {error}
      </div>
    );

  const user = business?.user || {};
  const image = user.image_url || "";
  const userType = user.status || "individual";
  const currentCurrency = currencies.find(
    (c) => c.short_name === business?.currency,
  );

  const fullName =
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.full_name ||
    "";
  const companyName = user.company_name || business?.company_name || "";
  const displayName =
    userType === "salon" && companyName ? companyName : fullName;
  const isSalonView = userType === "salon" || userType === "individual";

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-gray-100 transition-all"
        style={{ marginTop: "0px" }}
      >
        {userType === "salon" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-4"
          >
            <Building2 className="w-7 h-7 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              {t("home.myBusiness")}
            </h2>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex flex-wrap justify-between items-center gap-2 mb-6"
        >
          <motion.h3
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[22px] font-bold text-gray-900"
          >
            {displayName}
          </motion.h3>

          <PrimaryButton
            onClick={() => setShowModal(true)}
            label={t("home.edit")}
            rightIcon={<Pencil className="w-4 h-4" />}
          />
        </motion.div>

        {userType === "salon" && (
          <motion.p
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-[16px] text-gray-500 mt-[-10px] mb-6"
          >
            {user.first_name} {user.last_name}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 w-full"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="w-[110px] h-[110px] sm:w-[160px] sm:h-[160px] rounded-3xl overflow-hidden shadow-inner flex-shrink-0 border border-gray-100"
          >
            <img
              src={image}
              alt={t("home.businessAlt")}
              className="w-full h-full object-cover"
            />
          </motion.div>

          <div className="flex flex-col justify-between w-full">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-10"
            >
              {isSalonView && (
                <>
                  <InfoItem
                    icon={<Phone className="w-5 h-5 text-black" />}
                    title={t("home.phone")}
                    value={
                      business?.phone_number ? (
                        <a
                          href={`tel:${business.phone_number}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {business.phone_number}
                        </a>
                      ) : (
                        t("home.noPhone")
                      )
                    }
                  />
                  <InfoItem
                    icon={<Mail className="w-5 h-5 text-black" />}
                    title={t("home.email")}
                    value={
                      user.email ? (
                        <a
                          href={`mailto:${user.email}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {user.email}
                        </a>
                      ) : (
                        t("home.noEmail")
                      )
                    }
                  />
                  <InfoItem
                    icon={<MapPin className="w-5 h-5 text-black" />}
                    title={t("home.location")}
                    value={
                      business?.address_url ? (
                        <a
                          href={business.address_url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-black transition-colors cursor-pointer truncate block text-[14px]"
                          title={addressText || business.address_url}
                        >
                          {loadingAddress && !addressText
                            ? t("home.loadingAddress", "Loading address...")
                            : addressText || business.address_url}
                        </a>
                      ) : (
                        t("home.unknownAddress")
                      )
                    }
                  />
                  <InfoItem
                    icon={
                      currentCurrency ? (
                        <img
                          src={currentCurrency.icon}
                          alt={currentCurrency.short_name}
                          className="w-6 h-6 rounded-full border border-gray-200 object-cover"
                        />
                      ) : (
                        <DollarSign className="w-5 h-5 text-black" />
                      )
                    }
                    title={t("home.currency")}
                    value={
                      currentCurrency
                        ? currentCurrency.short_name
                        : business?.currency || "USD"
                    }
                  />
                </>
              )}

              {userType !== "worker" && (
                <>
                 {businessLink && <> <InfoItem
                    icon={<Globe className="w-5 h-5 text-black" />}
                    title={t("home.businessLink")}
                    value={
                      <div className="flex items-center gap-2 max-w-full">
                        <a
                          href={businessLink}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-black text-[#020637] transition-colors cursor-pointer truncate text-[14px]"
                          title={businessLink}
                        >
                          {businessLink}
                        </a>
                        <button
                          type="button"
                          onClick={handleCopyLink}
                          className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0 cursor-pointer"
                          title={t("home.copyLink")}
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    }
                  />
                  <InfoItem
                    icon={<ImagePlus className="w-5 h-5 text-black" />}
                    value={
                      <div className="flex items-center">
                        <PrimaryButton
                          onClick={() => setShowPhotosModal(true)}
                          label={t("home.businessPhotos")}
                          rightIcon={<ImagePlus className="w-4 h-4" />}
                        />
                      </div>
                    }
                  />
                  </>}
                </>
              )}

              {userType === "worker" && (
                <>
                  <InfoItem
                    icon={<Phone className="w-5 h-5 text-black" />}
                    title={t("home.phone")}
                    value={
                      business?.phone_number ? (
                        <a
                          href={`tel:${business.phone_number}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {business.phone_number}
                        </a>
                      ) : (
                        t("home.noPhone")
                      )
                    }
                  />
                  <InfoItem
                    icon={<Mail className="w-5 h-5 text-black" />}
                    title={t("home.email")}
                    value={
                      user.email ? (
                        <a
                          href={`mailto:${user.email}`}
                          className="hover:text-black transition-colors cursor-pointer"
                        >
                          {user.email}
                        </a>
                      ) : (
                        t("home.noEmail")
                      )
                    }
                  />
                </>
              )}
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {showModal && (
        <EditBusinessModal
          onClose={(updated) => {
            setShowModal(false);
            if (updated) fetchUserInfo();
          }}
          userData={business}
        />
      )}

      {showPhotosModal && (
        <BusinessPhotosModal
          isOpen={showPhotosModal}
          onClose={() => setShowPhotosModal(false)}
        />
      )}
    </>
  );
}

function InfoItem({ icon, title, value }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex items-start gap-3 group transition-all w-full min-w-0"
    >
      <div className="bg-gray-50 group-hover:bg-gray-100 border border-gray-100 w-10 h-10 rounded-xl flex justify-center items-center mt-0.5 flex-shrink-0 transition-all">
        {icon}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
          {title}
        </p>
        <div className="text-[15px] text-gray-800 font-semibold leading-snug break-all sm:break-words">
          {value}
        </div>
      </div>
    </motion.div>
  );
}
